import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Walks a build script: which question is on screen, what has been answered,
 * and what the trail has recorded.
 *
 * <h3>It records, it does not predict</h3>
 *
 * Only answered steps become nodes. Platform steps are appended by whoever
 * performs them, once their call has returned — this hook never writes one,
 * because it does not know whether the work happened.
 *
 * <h3>Resuming</h3>
 *
 * A `seed` of `{answers, at}` reopens a build that was saved half-finished. The
 * trail is rebuilt from the answers rather than restored from the draft, so
 * what the person sees above the question is the script's own words for what
 * they said, not a stale copy of them.
 *
 * It refuses to resume at or past the commit. A draft only exists while nothing
 * has been created; seeding past it would put somebody in front of a question
 * about an agent this session knows nothing about — including the one that
 * registers it, which would register a second.
 *
 * @param {Array}  script steps from data/buildScripts.js
 * @param {Object} opts   `{seed: {answers, at}}` to resume a saved build
 */
export function useBuildFlow(script = [], { seed = null } = {}) {
  /* How a saved build becomes a starting point. Pure, so it can be applied at
     mount or when the person accepts the nudge that offers it. */
  const resolve = useCallback((from) => {
    const at = script.findIndex((st) => st.key === from?.at);
    const commitAt0 = script.findIndex((st) => st.commit);
    if (at <= 0) return { i: 0, answers: {}, log: [] };
    if (commitAt0 >= 0 && at > commitAt0) return { i: 0, answers: {}, log: [] };
    const answered = script.slice(0, at);
    return {
      i: at,
      answers: { ...(from.answers || {}) },
      /* Built from the script, exactly as `answer` builds one, so a reopened
         trail and a walked one cannot read differently. */
      log: answered.map((st, n) => {
        const v = from.answers?.[st.key];
        const node = typeof st.node === 'function' ? st.node(v) : st.node;
        return { id: `${st.key}-${n}`, stepKey: st.key, stage: st.stage,
          icon: st.icon, ...node };
      }),
    };
  }, [script]);

  const [answers, setAnswers] = useState(() => resolve(seed).answers);
  const [log, setLog] = useState(() => resolve(seed).log);
  const [i, setI] = useState(() => resolve(seed).i);

  /**
   * A seed that arrives after mount — the person accepted the nudge offering a
   * saved build.
   *
   * Only while the sequence is untouched. Landing a draft on a walk somebody is
   * part-way through would move the question out from under them, and the
   * answers on screen are theirs, not the draft's.
   */
  const untouched = i === 0 && log.length === 0;
  useEffect(() => {
    if (!seed || !untouched) return;
    const next = resolve(seed);
    if (next.i === 0) return;
    setAnswers(next.answers);
    setLog(next.log);
    setI(next.i);
  }, [seed, untouched, resolve]);

  const asks = useMemo(() => script.filter((s) => s.ask), [script]);
  const step = script[i] || null;
  const complete = i >= script.length;

  /* The ask is the step's public face, so it carries the step's own markers:
     whether answering it commits anything, and which journey stage the agent
     is at while it is on screen. A caller that had to reach back into the
     script for those is a caller that can silently drop them. */
  /* Where the point of no return is, and whether it has been passed. Derived
     from the script's own marker so moving the commit moves the guard with it.
     A script with no commit step never freezes anything. */
  const commitAt = useMemo(() => script.findIndex((s) => s.commit), [script]);
  const committed = commitAt >= 0 && i > commitAt;

  const ask = step?.ask
    ? {
      ...step.ask,
      key: step.key,
      commit: step.commit === true,
      stage: step.stage,
      /* The icon travels with the step. The trail used to look it up by the
         answer's key against the Register step list, which only works while
         the two spell every step the same way — and they do not. */
      icon: step.icon,
      number: asks.findIndex((s) => s.key === step.key) + 1,
      total: asks.length,
    }
    : null;

  const answer = useCallback((value) => {
    const s = script[i];
    if (!s) return;
    const node = typeof s.node === 'function' ? s.node(value) : s.node;
    setAnswers((prev) => ({ ...prev, [s.key]: value }));
    /* `stepKey` is what lets the trail offer to change this step later. It is
       stamped from the script rather than inferred from the node's wording,
       which would break the moment a node's title is edited. */
    /* The node carries the stage it belongs to. The trail groups by it, so a
       node without one would fall out of the journey it is part of — which is
       how Identity and Intent came to look like they were never visited: their
       nodes were in the list, just not under a heading that said so. */
    setLog((prev) => [...prev,
      { id: `${s.key}-${prev.length}`, stepKey: s.key, stage: s.stage,
        icon: s.icon, ...node }]);
    setI((n) => n + 1);
  }, [script, i]);

  /**
   * Go back to an answered step, before anything has been committed.
   *
   * Refused outright for any step at or before the commit once the commit has
   * happened — see the guard below for why.
   *
   * The nodes below it record answers that have not been given again, so the
   * trail is cut back to this step — a record must not show things that are no
   * longer true. The answers themselves are kept, so the reopened question
   * shows what was chosen rather than an empty field.
   */
  const reopen = useCallback((key) => {
    const at = script.findIndex((s) => s.key === key);
    if (at < 0 || at >= i) return;
    /* Nothing that produced the agent may be reopened once it exists.
       Answering the commit step a second time fires the onboard a second time —
       a second agent row, a second registration and a second set of minted
       keys, with the first orphaned and nobody told. The console draws the same
       line: its Back button is disabled past the mint because the credentials
       are already in the database. The rule lives here rather than in the trail
       because the hook owns the sequence; a view that merely stops drawing the
       link leaves the door open for the next caller. */
    if (committed && at <= commitAt) return;
    setI(at);
    setLog((prev) => {
      const cut = prev.findIndex((e) => e.stepKey === key);
      return cut < 0 ? prev : prev.slice(0, cut);
    });
  }, [script, i, committed, commitAt]);

  /**
   * Whether a step can still be reopened — the same rule `reopen` enforces,
   * asked rather than re-derived. A view that kept its own copy of where the
   * commit is would offer a link the hook then declines.
   */
  const canReopen = useCallback((key) => {
    const at = script.findIndex((s) => s.key === key);
    if (at < 0 || at >= i) return false;
    return !(committed && at <= commitAt);
  }, [script, i, committed, commitAt]);

  /** Append a step the platform performed, once its call has returned. */
  const record = useCallback((node) => {
    setLog((prev) => [...prev, { id: `did-${prev.length}`, ...node }]);
  }, []);

  return { ask, answers, log, complete, committed, canReopen, answer, record, reopen };
}

export default useBuildFlow;
